import { Box, Checkbox, CheckboxGroup, Flex, Icon, Stack, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FaPhone, FaSms } from 'react-icons/fa';
import { MdAccessTime } from 'react-icons/md';

import { TWILIO_FROM } from '#constants';
import { useUIStore } from '#store';
import CallRangePicker from '../Profile/CallRangePicker.jsx';

export default function TrackerNotification() {
  const {
    notificationPref,
    setNotificationPreference,
    updateUserPref,
    user,
  } = useUIStore((state) => ({
    notificationPref: state.trackerInput?.notificationPref,
    setNotificationPreference: state.setNotificationPreference,
    updateUserPref: state.updateUserPref,
    user: state.user,
  }));
  const defaultValue = [user?.preference?.callRange?.start ?? 8, user?.preference?.callRange?.end ?? 18];
  const [range, setRange] = useState(defaultValue);
  const updateUser = (val) => {
    setRange(val);
    updateUserPref({
      preference: {
        callRange: {
          start: val[0],
          end: val[1],
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    });
  };
  // Set default call range + timezone when user is loaded and has none (e.g. first checkout).
  useEffect(() => {
    if (
      user?._id &&
      !user?.preference?.callRange?.start &&
      !user?.preference?.callRange?.end
    )
      updateUserPref({
        preference: {
          callRange: {
            start: 8,
            end: 18,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        },
      });
  }, [user?._id, user?.preference?.callRange?.start, user?.preference?.callRange?.end]);

  const selected = notificationPref ?? [];
  const isCallSelected = selected.indexOf('call') > -1;

  // Every tracker starts with both channels. Run once so a later uncheck sticks.
  useEffect(() => {
    const pref = notificationPref ?? [];
    if (pref.indexOf('text') > -1 && pref.indexOf('call') > -1) return;
    setNotificationPreference(['text', 'call']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPrefChange = (vals) => {
    const next = ['text'];
    if ((vals || []).indexOf('call') > -1) next.push('call');
    setNotificationPreference(next);
  };

  return (
    <Box
      p={5}
      borderRadius="lg"
      bg="gray.50"
      borderLeft="4px solid"
      borderColor="brand.500"
      transition="box-shadow 0.2s"
      _hover={{ shadow: 'sm' }}
    >
      <Text
        fontSize="xs"
        fontWeight="semibold"
        letterSpacing="wider"
        color="gray.500"
        textTransform="uppercase"
        mb={3}
      >
        Notification Preferences
      </Text>

      <CheckboxGroup
        colorScheme="brand"
        value={selected.indexOf('text') > -1 ? selected : ['text', ...selected]}
        onChange={onPrefChange}
      >
        <Stack spacing={3}>
          <Text fontSize="sm" fontWeight="medium" color="gray.700">
            How would you like to be notified?
          </Text>

          <Flex gap={6} flexWrap="wrap">
            <Checkbox
              value="text"
              isChecked
              isReadOnly
              size="lg"
              spacing={3}
              colorScheme="brand"
            >
              <Flex align="center" gap={2}>
                <Icon as={FaSms} boxSize={4} color="brand.500" />
                <Text fontSize="sm" fontWeight="medium">Text Message</Text>
              </Flex>
            </Checkbox>
            <Checkbox
              value="call"
              isChecked={isCallSelected}
              size="lg"
              spacing={3}
              colorScheme="brand"
            >
              <Flex align="center" gap={2}>
                <Icon as={FaPhone} boxSize={4} color="brand.500" />
                <Text fontSize="sm" fontWeight="medium">Phone Call</Text>
              </Flex>
            </Checkbox>
          </Flex>
        </Stack>
      </CheckboxGroup>

      <Flex
        align="center"
        gap={2}
        mt={3}
        p={3}
        bg="white"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
      >
        <Icon as={FaPhone} boxSize={3} color="gray.400" />
        <Text fontSize="xs" color="gray.600">
          Notifications will come from{' '}
          <Text as="span" fontWeight="semibold" color="gray.700">
            {TWILIO_FROM}
          </Text>
        </Text>
      </Flex>

      <Box
        visibility={isCallSelected ? 'visible' : 'hidden'}
        opacity={isCallSelected ? 1 : 0}
        maxHeight={isCallSelected ? '200px' : 0}
        overflow="hidden"
        transition="all 0.3s ease-out"
        mt={isCallSelected ? 2 : 0}
        pt={isCallSelected ? 2 : 0}
        borderTop={isCallSelected ? '1px solid' : 'none'}
        borderColor="gray.200"
      >
        <Flex align="center" gap={2} mb={1}>
          <Icon as={MdAccessTime} boxSize={4} color="brand.600" />
          <Text fontSize="sm" fontWeight="semibold" color="brand.800">
            Preferred calling hours
          </Text>
        </Flex>

        <CallRangePicker start={range[0]} end={range[1]} onChangeEnd={updateUser} />
      </Box>
    </Box>
  );
}
